import { Link } from "@tanstack/react-router";
import { Phone, MessageCircle, MapPin, ArrowRight, CheckCircle, Calendar } from "lucide-react";
import { getBusinessConfig } from "@/lib/site-config";
import { trackEvent } from "@/lib/tracking";
import { FAQAccordion } from "@/components/FAQAccordion";
import { WhyChooseWoodseer } from "@/components/WhyChooseWoodseer";

export interface DriverLandingContent {
  eyebrow: string;
  h1: string;
  intro: string;
  trustChips: string[];
  audienceTitle: string;
  audience: string[];
  helpTitle: string;
  helpItems: string[];
  issuesTitle: string;
  issues: string[];
  valueTitle: string;
  valueBody: string;
  beforeTitle: string;
  beforeItems: string[];
  faqs: { q: string; a: string }[];
  bookingContext: string;
  waPrefill: string;
  relatedLinks: { to: string; label: string }[];
}

interface Props {
  content: DriverLandingContent;
  path: string;
}

/**
 * Shared layout for high-intent driver landing pages
 * (PCO / Uber & Bolt / value-for-money). All CTAs tracked,
 * booking + WhatsApp prefilled with context.
 */
export function DriverLandingPage({ content, path }: Props) {
  const biz = getBusinessConfig();
  const meta = { location: path, context: content.bookingContext };
  const bookHref = `/book?service=mot&context=${encodeURIComponent(content.bookingContext)}`;
  const wa = `https://wa.me/${biz.phone.replace(/\D/g, "")}?text=${encodeURIComponent(content.waPrefill)}`;

  return (
    <>
      {/* Hero */}
      <section className="py-14 md:py-20 bg-secondary">
        <div className="mx-auto max-w-4xl px-4">
          <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-primary">
            {content.eyebrow}
          </p>
          <h1 className="mt-3 text-3xl md:text-5xl font-extrabold text-foreground tracking-tight leading-tight">
            {content.h1}
          </h1>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed max-w-3xl">
            {content.intro}
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            <a
              href={biz.phoneHref}
              onClick={() => trackEvent("call_click", meta)}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:bg-primary/90"
            >
              <Phone className="h-4 w-4" /> Call {biz.phone}
            </a>
            <a
              href={wa}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("whatsapp_click", meta)}
              className="inline-flex items-center gap-2 rounded-lg bg-[#25D366] px-5 py-3 text-sm font-bold text-white hover:bg-[#1ea952]"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
            <a
              href={bookHref}
              onClick={() => trackEvent("booking_request_start", meta)}
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-5 py-3 text-sm font-bold text-foreground hover:border-primary/40"
            >
              <Calendar className="h-4 w-4" /> Request booking
            </a>
            <a
              href={biz.googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-5 py-3 text-sm font-bold text-foreground hover:border-primary/40"
            >
              <MapPin className="h-4 w-4" /> Directions
            </a>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-y border-border bg-card py-4">
        <div className="mx-auto max-w-5xl px-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs font-semibold text-muted-foreground">
          {content.trustChips.map((c) => (
            <span key={c} className="inline-flex items-center gap-1.5">
              <CheckCircle className="h-3.5 w-3.5 text-primary" />
              {c}
            </span>
          ))}
        </div>
      </section>

      {/* Audience + Help we cover */}
      <section className="py-12 md:py-14">
        <div className="mx-auto max-w-5xl px-4 grid md:grid-cols-2 gap-8">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-foreground">{content.audienceTitle}</h2>
            <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
              {content.audience.map((a) => (
                <li key={a} className="flex gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                  {a}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-foreground">{content.helpTitle}</h2>
            <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
              {content.helpItems.map((h) => (
                <li key={h} className="flex gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                  {h}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Common issues */}
      <section className="py-12 md:py-14 bg-secondary/40">
        <div className="mx-auto max-w-4xl px-4">
          <h2 className="text-xl md:text-2xl font-bold text-foreground">{content.issuesTitle}</h2>
          <div className="mt-5 grid sm:grid-cols-2 gap-3">
            {content.issues.map((i) => (
              <div key={i} className="rounded-xl border border-border bg-card p-4 text-sm text-foreground">
                {i}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value statement */}
      <section className="py-12 md:py-14">
        <div className="mx-auto max-w-3xl px-4">
          <h2 className="text-xl md:text-2xl font-bold text-foreground">{content.valueTitle}</h2>
          <p className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed">
            {content.valueBody}
          </p>
        </div>
      </section>

      {/* Before you arrive / Weekly checks */}
      <section className="py-12 md:py-14 bg-card">
        <div className="mx-auto max-w-3xl px-4">
          <h2 className="text-xl md:text-2xl font-bold text-foreground">{content.beforeTitle}</h2>
          <ul className="mt-5 grid sm:grid-cols-2 gap-2 text-sm text-muted-foreground">
            {content.beforeItems.map((b) => (
              <li key={b} className="flex gap-2">
                <CheckCircle className="mt-0.5 h-4 w-4 text-primary shrink-0" />
                {b}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Strong CTA banner */}
      <section className="py-12 bg-secondary">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground">
            Need a hand from a real garage?
          </h2>
          <p className="mt-3 text-sm md:text-base text-muted-foreground">
            DVSA-authorised MOT station in Poplar, E14 — five minutes from Canary Wharf. Honest advice before any work.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <a
              href={biz.phoneHref}
              onClick={() => trackEvent("call_click", { ...meta, action: "bottom_banner" })}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground"
            >
              <Phone className="h-4 w-4" /> Call {biz.phone}
            </a>
            <a
              href={wa}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("whatsapp_click", { ...meta, action: "bottom_banner" })}
              className="inline-flex items-center gap-2 rounded-lg bg-[#25D366] px-5 py-3 text-sm font-bold text-white"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
            <a
              href={bookHref}
              onClick={() => trackEvent("booking_request_start", { ...meta, action: "bottom_banner" })}
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-5 py-3 text-sm font-bold text-foreground"
            >
              <Calendar className="h-4 w-4" /> Request MOT booking
            </a>
          </div>
        </div>
      </section>

      <WhyChooseWoodseer page={path.replace(/^\//, "") || "driver-landing"} />

      <FAQAccordion faqs={content.faqs} title="Frequently asked questions" />

      {content.relatedLinks.length > 0 && (
        <section className="py-12">
          <div className="mx-auto max-w-3xl px-4">
            <h2 className="text-base font-bold text-foreground">Related</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {content.relatedLinks.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="inline-flex items-center gap-1 rounded-lg border border-border bg-card px-3 py-2 text-sm font-semibold text-foreground hover:border-primary/40"
                >
                  {l.label} <ArrowRight className="h-3 w-3" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
